import type { ColumnDef } from '@tanstack/react-table'
import { Link } from 'react-router-dom'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Tooltip } from '@/components/ui/tooltip'
import { setCreatorId } from '@/models/creators.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { detailedComparisonTableCellFormatter } from '@/modules/overview/ui/DetailedComparison/utils.ts'
import { AddTableRowButton } from '@/modules/sales-editor/components/add-table-row-button'
import { TableCellEditor } from '@/modules/sales-editor/components/table-cell-editor'
import { AppRoutes } from '@/utils/constants'

import type { DetailedComparisonType } from '../../types'
import { detailedComparisonTableColumnTitles } from './consts'

export const detailedComparisonColumns: ColumnDef<DetailedComparisonType>[] = [
  {
    accessorKey: 'creator',
    header: () => (
      <div className="relative">
        {detailedComparisonTableColumnTitles.creator}
        <AddTableRowButton />
      </div>
    ),
    cell: (ctx) => (
      <div className="inline-flex w-fit items-center justify-center px-0">
        <Link
          onClick={() => {
            setCreatorId(ctx.row.original.creatorId)
          }}
          to={AppRoutes.Creator.Statistics.OnlyFans.asUrl(
            ctx.row.original.creatorId,
          )}
          className="hover:text-primary-500 flex items-center gap-3 hover:no-underline"
        >
          <div className="relative">
            <CreatorAvatar
              name={ctx.cell.getValue<string>()}
              avatarSrc={ctx.row.original.creatorProfilePicture}
              className="h-10 w-10"
            />
          </div>
          <span className="truncate">
            <TableCellEditor
              className="min-w-[150px]"
              cellContext={ctx}
            />
          </span>
        </Link>
      </div>
    ),
  },
  {
    accessorKey: 'totalRevenue',
    header: () => (
      <Tooltip
        content="Shows the total revenue the creators page has acquired during the selected timeframe, including all messages and tips"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.totalRevenue}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'newSubs',
    header: () => (
      <Tooltip
        content="Shows the number of new first-time subscribers the creators page has acquired during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.newSubs}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'newSubscriptionsRevenue',
    header: () => (
      <Tooltip
        content="Shows the amount of revenue from all new first-time subscribers the creators page has acquired during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.newSubscriptionsRevenue}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'recurringSubscriptionsRevenue',
    header: () => (
      <Tooltip
        content="Shows the amount of revenue from all recurring subscribers the creators page has acquired during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.recurringSubscriptionsRevenue}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'tipsRevenue',
    header: () => (
      <Tooltip
        content="Shows the amount of revenue generated through all types of tips the creators page has acquired during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.tipsRevenue}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'messageRevenue',
    header: () => (
      <Tooltip
        content="Shows the amount of revenue generated through all pay-per-view sales the creators page has acquired during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.messageRevenue}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'textingRatio',
    header: () => (
      <Tooltip
        content="The money earned from tips and messages, divided by the revenue from subscribers"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.textingRatio}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'openChats',
    header: () => (
      <Tooltip
        content="Shows the amount of chats where the subscriber actively responded on the creators page during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.openChats}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'sellingChats',
    header: () => (
      <Tooltip
        content="Shows the amount of chats where the subscriber tipped or bought any content on the creators page during the slected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.sellingChats}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'averageFanSpend',
    header: () => (
      <Tooltip
        content="Measures the average spend per fan of any fan that spend money during the selected period"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.averageFanSpend}
        <InfoIcon className="h-4 w-4 flex-shrink-0" />
      </Tooltip>
    ),
    cell: (ctx) => (
      <Tooltip
        content={
          ctx.row.getValue('averageFanSpend')
            ? `Total revenue $${ctx.row.original.totalRevenue} by ${ctx.row.original.spendingFansCount} fans`
            : ''
        }
        className="inline-flex w-fit items-center justify-center px-0"
      >
        <TableCellEditor
          cellContext={ctx}
          format={detailedComparisonTableCellFormatter(ctx, true)}
        />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'linkClicks',
    header: () => (
      <Tooltip
        content="Shows the amount of link clicks connected to your stayundercover account or any other account you have attatched to the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.linkClicks}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
  {
    accessorKey: 'conversionRate',
    header: () => (
      <Tooltip
        content="Shows the conversion rate resulting from the new Subscribers divided by the Link Clicks during the selected timeframe"
        className="flex items-center justify-center gap-1"
      >
        {detailedComparisonTableColumnTitles.conversionRate}
        <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
      </Tooltip>
    ),
  },
]
